import { motion } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";

const OPTIONS = [
  {
    key: "video",
    title: "Souvenir vidéo 4K pro",
    desc:
      "Captation professionnelle 4K de votre journée — cérémonie, première danse, discours. Un film qui restera pour la vie.",
    image:
      "https://static.wixstatic.com/media/53b2bd_5d74d1b0e9da491184f622aa479eec7c~mv2.jpg/v1/fill/w_900,h_600,al_c,q_85/2videaste.jpg",
  },
  {
    key: "live-screen",
    title: "Retransmission live sur écran géant",
    desc:
      "Vos invités voient l'action en temps réel : cérémonie, ouverture de bal, discours… Diffusion HD sur écran géant 3 × 3 m.",
    image:
      "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80",
  },
  {
    key: "lumiere",
    title: "Mise en lumière LED",
    desc:
      "+360 déclinaisons de couleurs pour habiller votre salle. Éclairage indirect, ambiance tamisée et personnalisée.",
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/4ddkhmgi_WhatsApp%20Image%202026-05-12%20at%2011.34.59.jpeg",
  },
  {
    key: "etincelles",
    title: "Étincelles froides",
    desc:
      "Effet spectaculaire sans aucun danger — parfait pour la première danse ou l'entrée des mariés.",
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/fh2an4yw_WhatsApp%20Image%202026-05-12%20at%2011.34.jpeg",
  },
  {
    key: "fumee",
    title: "Fumée lourde au sol",
    desc:
      "Une fumée dense et féerique qui se répand au sol pour un effet conte de fée pendant l'ouverture de bal.",
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/yeuegh8j_IMG_8908.jpeg",
  },
  {
    key: "album-usb",
    title: "Album photos & USB souvenir",
    desc:
      "Album HD luxe 30 × 30 cm + l'intégralité de vos photos en haute définition sur clé USB personnalisée.",
    image:
      "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=1200&q=80",
  },
  {
    key: "photobooth",
    title: "Photobooth",
    desc:
      "Borne photo libre-service avec impression instantanée. Vos invités repartent avec un souvenir physique de votre soirée.",
    image:
      "https://images.unsplash.com/photo-1496337589254-7e19d01cec44?auto=format&fit=crop&w=1200&q=80",
  },
  {
    key: "caricaturiste",
    title: "Caricaturiste",
    desc:
      "Un dessinateur professionnel crée des portraits hilarants et personnalisés en direct pour vos invités.",
    image:
      "https://static.wixstatic.com/media/53b2bd_7fac3b6c07f441d2997b6abf3e84b2d3~mv2.jpg/v1/fill/w_900,h_600,al_c,q_85/IMG_2610_JPG.jpg",
  },
  {
    key: "magicien",
    title: "Magicien · Mentaliste · Ténor",
    desc:
      "Un trio d'exception pour étonner, captiver et émouvoir vos invités à des moments-clés de la soirée.",
    image:
      "https://static.wixstatic.com/media/4d72201313404844849f771c0135833b.jpg/v1/fill/w_900,h_600,al_c,q_85/Magicien.jpg",
  },
];

export const SignatureOptions = () => {
  return (
    <section
      id="options"
      data-testid="signature-options-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(212,175,55,0.10),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow inline-flex items-center gap-2">
            <Star size={13} /> Mes points forts
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Des options <span className="gold-text-gradient">qui marquent les esprits.</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Au-delà du DJ set, je propose une gamme complète d'options scéniques et
            d'animations pour transformer votre événement en moment d'exception.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 lg:auto-rows-[280px]">
          {OPTIONS.map((o, i) => (
            <motion.div
              key={o.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`option-${o.key}`}
              className={`relative overflow-hidden rounded-2xl group border border-white/10 hover:border-[#D4AF37]/40 transition-all ${
                o.span || ""
              }`}
            >
              <img
                src={o.image}
                alt={o.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10 group-hover:from-black/95 transition-colors" />

              <div className="relative z-10 h-full min-h-[280px] p-6 md:p-7 flex flex-col justify-end">
                <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                  {o.title}
                </h3>
                <p className="mt-2.5 text-sm text-zinc-300 leading-relaxed max-w-md">
                  {o.desc}
                </p>
                <a
                  href="#contact"
                  data-testid={`option-cta-${o.key}`}
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E9C766] font-medium opacity-90 group-hover:opacity-100"
                >
                  Ajouter à mon devis <ArrowRight size={14} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="#contact"
            data-testid="signature-options-cta-quote"
            className="btn-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center justify-center gap-2"
          >
            Composer mon événement <ArrowRight size={18} />
          </a>
          <a
            href="https://www.asanimation61.com/pointfortdjasanimation"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signature-options-fullpage"
            className="text-sm text-zinc-400 hover:text-[#D4AF37] underline-offset-4 hover:underline"
          >
            Voir tous les détails
          </a>
        </div>
      </div>
    </section>
  );
};
